import { useEffect, useState } from "react";
import { useLingui } from "@lingui/react";
import { useRouterState } from "@tanstack/react-router";
import { getEnv } from "@ando/core/env";
import {
  CONSENT_EVENT,
  configureAnalytics,
  readConsent,
  saveConsent,
  suspendAnalytics,
  trackPage,
} from "@ando/core/analytics";
import {
  AnalyticsConsentPanel,
  AnalyticsSettingsButton,
  type AnalyticsConsentStrings,
} from "@ando/ui/analytics";
import { platformPath } from "./routes";

export const PLATFORM_CONSENT_KEY = "ando.platform-analytics-consent.v1";
const SETTINGS_EVENT = "ando:platform-analytics-settings";
const id = import.meta.env.VITE_GA_MEASUREMENT_ID ?? "";
const copy: Record<string, AnalyticsConsentStrings> = {
  it: {
    title: "Analytics della piattaforma",
    notice:
      "Con il tuo consenso misuriamo le schermate aperte in Workspace e Console con Google Analytics. Non inviamo contenuti, ricerche, nomi o identificativi dei tuoi dati. Puoi rifiutare o cambiare scelta in Privacy.",
    learnMore: "Informativa privacy",
    necessaryOnly: "Solo necessari",
    acceptAnalytics: "Accetta analytics",
    settings: "Preferenze analytics",
  },
  en: {
    title: "Platform analytics",
    notice:
      "With your consent, Google Analytics measures screens opened in Workspace and Console. We do not send content, searches, names or identifiers from your data. You can decline or change your choice in Privacy.",
    learnMore: "Privacy notice",
    necessaryOnly: "Necessary only",
    acceptAnalytics: "Accept analytics",
    settings: "Analytics preferences",
  },
};
export function analyticsAvailable(): boolean {
  return (
    import.meta.env.PROD &&
    /^G-[A-Z0-9]+$/.test(id) &&
    !getEnv().isTauri &&
    ["ando-ai.com", "www.ando-ai.com"].includes(window.location.hostname)
  );
}
export function openPlatformAnalyticsSettings(): void {
  window.dispatchEvent(new Event(SETTINGS_EVENT));
}
export function PlatformAnalyticsSettings() {
  const { i18n } = useLingui();
  if (!analyticsAvailable()) return null;
  return (
    <AnalyticsSettingsButton
      label={(copy[i18n.locale] ?? copy.en)!.settings}
      onClick={openPlatformAnalyticsSettings}
    />
  );
}
export function PlatformAnalytics() {
  const { i18n } = useLingui();
  const screen = useRouterState({
    select: (state) => ({
      idle: state.status === "idle",
      pathname: state.resolvedLocation?.pathname,
      authenticated: state.matches.some((match) => match.routeId === "/_authed"),
    }),
  });
  const [show, setShow] = useState(false);
  const [consent, setConsent] = useState<boolean | null>(() => readConsent(PLATFORM_CONSENT_KEY));
  const path = screen.pathname ? platformPath(screen.pathname) : null;
  const allowed = analyticsAvailable() && screen.authenticated && screen.idle && !!path;
  useEffect(() => {
    const sync = () => {
      const current = readConsent(PLATFORM_CONSENT_KEY);
      setConsent(current);
      setShow(current === null);
    };
    const reopen = () => setShow(true);
    sync();
    window.addEventListener(CONSENT_EVENT, sync);
    window.addEventListener("storage", sync);
    window.addEventListener(SETTINGS_EVENT, reopen);
    return () => {
      window.removeEventListener(CONSENT_EVENT, sync);
      window.removeEventListener("storage", sync);
      window.removeEventListener(SETTINGS_EVENT, reopen);
    };
  }, []);
  useEffect(() => {
    if (!analyticsAvailable() || !screen.idle) return;
    if (consent !== true) {
      configureAnalytics(id, false, { consentKey: PLATFORM_CONSENT_KEY });
      return;
    }
    if (!allowed || !path) {
      suspendAnalytics(id);
      return;
    }
    const location = `${window.location.origin}${path}`;
    configureAnalytics(id, true, {
      location,
      consentKey: PLATFORM_CONSENT_KEY,
      surface: "platform",
    });
    trackPage(location);
  }, [allowed, path, consent, screen.idle]);
  if (!allowed || !show) return null;
  return (
    <AnalyticsConsentPanel
      strings={(copy[i18n.locale] ?? copy.en)!}
      href="/privacy"
      onChoose={(accepted) => {
        saveConsent(accepted, PLATFORM_CONSENT_KEY);
        setShow(false);
      }}
    />
  );
}
